import { useEffect, useRef } from "react";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { holdPlayback, releasePlayback } from "utils/playbackHold";
import { originalPositionMs } from "utils/originalMedia";
import { consumeAlignedStart } from "utils/playbackIntent";

interface UseAudioPlayProps {
    /** The element that sounds. Owned by the row, not by the drawing library. */
    media: HTMLMediaElement | null;
    start: number;
    id: string;
    length: number;
}

/**
 * In sync. Below this nothing is done at all.
 *
 * The first version used 150ms, reasoning that lip-sync tolerance is wider than that. That is
 * true when you watch one track; it is wrong here, where both languages are audible at once.
 * Measured on the deployed build, the error parked at a median of -159ms — just under the
 * threshold, so the correction never fired and the voicing sat audibly behind for the whole
 * session. The threshold WAS the lag.
 */
const IN_SYNC_MS = 40;

/** How hard to nudge. 3% is well under the ~5% where pitch change becomes noticeable. */
const RATE_TRIM = 0.03;

/**
 * How far ahead of the lecturer the voicing may run before it is reined in.
 *
 * Deliberately generous. The clip starts fractionally early and the original waits for it, so a
 * lead is the intended state, not an error — pulling it back to zero would reintroduce the late
 * entry the hold exists to prevent. Falling behind, by contrast, is corrected immediately.
 */
const LEAD_ALLOWANCE_MS = 400;

/** Sound has genuinely started once the element has advanced this far. */
const STARTED_MS = 40;

/** How often to look for that, while a clip is starting. */
const WATCH_MS = 15;

/**
 * How long the original waits on a clip that is not yet even trying to play.
 *
 * play() is issued the moment the turn arrives, so there is a brief interval where the element is
 * still paused and nothing is wrong. Past that, a still-paused element means something has decided
 * it should not sound, and waiting on it would stall the lecture.
 *
 * There used to be a second, much longer ceiling here for a clip that was still LOADING. It is
 * gone: a cue is not handed over at all until useVoicingSchedule has established that it can make
 * sound, so by the time this hook runs the bytes are in the element already. Waiting for them
 * twice, in two places, with two different rules, is what made the waiting unpredictable.
 */
const HOLD_GRACE_MS = 400;

/**
 * PLAY THIS CLIP WHEN IT IS ITS TURN. NOTHING ELSE.
 *
 * Whose turn it is belongs to useVoicingSchedule, which reads the original's playhead; this hook
 * only carries the decision out on one element. While each fragment also selected itself, several
 * could answer "me" for the same moment, and the symptoms followed: a voicing playing twice, a
 * short one replaced before it was audible, playback deadlocking.
 *
 * The two jobs below sit on deliberately separate clocks. Starting a clip may pause the original
 * for a moment, and the first version watched for the start on the original's own tick — so the
 * hold stopped the very clock that would have released it, and every cue cost the full timeout.
 * Measured: a 1198ms median hold against a 1200ms cap.
 */
export const useAudioPlay = ({ media, start, id, length }: UseAudioPlayProps) => {
    const state = useAudioStateContext();
    /** Has this clip actually started making sound since its turn came? */
    const startedRef = useRef(false);

    // Reviewing re-voiced clips (Ctrl+U) plays fragments with the original stopped, so "should
    // this be sounding" is not the same question as "is the lecture running".
    const shouldSound = state.playing || state.playSavedTrack;
    const isCurrent = state.playingFragment === id;

    // ── the speed slider, on its own ─────────────────────────────────────────
    //
    // This used to live in the deps of the effect below, so moving the slider re-ran the whole
    // turn sequence: seek to zero, play() again — a sentence that was already halfway through
    // started over. Rate is a property of the element, not an instruction to begin.
    useEffect(() => {
        if (!media) return;
        const baseRate = Number(state.playbackRate) || 1;
        if (media.playbackRate !== baseRate) media.playbackRate = baseRate;
    }, [media, state.playbackRate]);

    // ── whether this clip is sounding at all ─────────────────────────────────
    useEffect(() => {
        if (!media) return;
        const baseRate = Number(state.playbackRate) || 1;

        if (!isCurrent || !shouldSound) {
            startedRef.current = false;
            releasePlayback(id);
            try {
                if (!media.paused) media.pause();
                if (media.currentTime) media.currentTime = 0; // ready for its next turn
            } catch {
                /* nothing to do */
            }
            if (media.playbackRate !== baseRate) media.playbackRate = baseRate;
            return;
        }

        // ITS TURN — from the beginning, always.
        //
        // A voicing is a sentence; entering it part-way to satisfy a clock sounds worse than the
        // lateness that would repair. Whatever lateness there is gets paid by the original, which
        // stops HERE, before play() is even issued, and does not move again until this clip is
        // making sound. The voicing therefore leads and the original follows it, rather than the
        // voicing chasing a lecturer who has already started talking.
        //
        // The exception is an aligned start — a reposition landing mid-cue. The clip enters at
        // the words matching the clicked moment, so there is no clean sentence start to protect
        // and nothing for the original to wait for; holding it there was itself a hiccup.
        const alignedMs = consumeAlignedStart(id);
        if (alignedMs === null) holdPlayback(id);
        startedRef.current = false;
        try {
            const startSec = alignedMs === null ? 0 : Math.max(0, alignedMs / 1000);
            media.currentTime =
                Number.isFinite(media.duration) && media.duration > 0
                    ? Math.min(startSec, Math.max(0, media.duration - 0.01))
                    : startSec;
        } catch {
            /* not seekable yet — it is at zero in practice */
        }
        // The clip's audibility on an iPad lives on `muted`: element volume is read-only there,
        // so «перевод в ноль» (the crossfade's left stop, or the Alt+Ctrl original-only boost)
        // must arrive as muted=true or the voicing keeps sounding at full blast. On desktop the
        // volume path also works; muted at zero is simply exact on both.
        media.muted = (Number(state.volumeTranslated) || 0) === 0;
        media.playbackRate = baseRate;
        const started = media.play();
        if (started && typeof started.catch === "function") {
            started.catch(() => {
                /* interrupted by the next turn, or blocked; the watchdog gives up on its own */
            });
        }

        const becameCurrent = Date.now();
        // Let go the moment sound is coming out — checked on the element's own events as well as
        // on a timer, because an event arrives sooner than the next poll.
        const check = () => {
            if (startedRef.current) return true;
            if (!media.paused && media.currentTime * 1000 > STARTED_MS) {
                startedRef.current = true;
                releasePlayback(id);
                return true;
            }
            return false;
        };
        media.addEventListener("timeupdate", check);
        const watchdog = window.setInterval(() => {
            if (check()) {
                window.clearInterval(watchdog);
                return;
            }
            if (alignedMs !== null) return; // a mid-sentence entry never holds the original
            const starting = Date.now() - becameCurrent < HOLD_GRACE_MS;
            if (starting || !media.paused) {
                holdPlayback(id);
            } else {
                // Not running, past the grace period: it is not going to play, and the lecture
                // must not go on waiting for it.
                releasePlayback(id);
                window.clearInterval(watchdog);
            }
        }, WATCH_MS);

        return () => {
            media.removeEventListener("timeupdate", check);
            window.clearInterval(watchdog);
            releasePlayback(id);
        };
        // state.playbackRate is deliberately absent — see the effect above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCurrent, shouldSound, media, id]);

    // ── holding it in step with the original ─────────────────────────────────
    useEffect(() => {
        if (!media || !isCurrent || !state.playing || !startedRef.current) return;
        if (media.paused || media.ended) return; // has not started, or has said its piece

        const baseRate = Number(state.playbackRate) || 1;
        const master = originalPositionMs();
        if (master === null || master <= 0) return;

        const wantSec = (master - start) / 1000;
        if (wantSec < 0 || wantSec > length / 1000) return; // the original has moved past us

        const haveSec = media.currentTime;
        if (!Number.isFinite(haveSec)) return;

        // THE VOICING IS ALLOWED TO LEAD.
        //
        // It starts a little before the lecturer reaches the line, and the original waits while it
        // gets going, so by design it runs slightly ahead. Correcting that back to zero would undo
        // the very thing the hold buys — the translation arriving with the line rather than after
        // it. Leading is tolerated up to LEAD_ALLOWANCE_MS; lagging is not tolerated at all.
        const driftMs = (haveSec - wantSec) * 1000;
        if (driftMs >= -IN_SYNC_MS && driftMs <= LEAD_ALLOWANCE_MS) {
            if (media.playbackRate !== baseRate) media.playbackRate = baseRate;
            return;
        }

        // Converge silently: fractionally slower when ahead of the original, fractionally faster
        // when behind, re-evaluated every tick. This is the only correction that runs while a clip
        // is sounding — seeking one mid-sentence is audible, and was itself reported as a fault.
        const target = driftMs > LEAD_ALLOWANCE_MS ? baseRate * (1 - RATE_TRIM) : baseRate * (1 + RATE_TRIM);
        if (Math.abs(media.playbackRate - target) > 0.001) media.playbackRate = target;
    }, [state.playbackTime, state.playing, state.playbackRate, media, isCurrent, start, length]);

    // Volume can hit zero WHILE a clip is sounding (the Alt+Ctrl boost does exactly that), and
    // the turn effect above deliberately does not re-run then — restarting the sentence for a
    // volume change was itself a reported fault. Mirror the zero-endpoint onto muted reactively.
    useEffect(() => {
        if (!media) return;
        const silent = (Number(state.volumeTranslated) || 0) === 0;
        if (media.muted !== silent) media.muted = silent;
    }, [media, state.volumeTranslated]);

    // A clip that disappears mid-hold must not keep the original waiting.
    useEffect(() => () => releasePlayback(id), [id]);
};
